import PencomTextField from "@/components/input/PencomTextField"
import PencomDateField from "@/components/input/PencomDateField"
import { PrimaryButton } from "@/components/buttons/PrimaryButton"
import { PencomBanner } from "@/components/banners/PencomBanner"
import { useState, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { PencomRoutes } from "@/router/PencomRoutes"
import { useCreateChangeRequest } from "@/hooks/employees/employees.hook"
import ChangeRequestService from "@/services/change-request/change-request.service"
import { ChangeRequestType } from "@/constants/enums/change-request.enum"

interface EmployeeFormData {
    surname: string;
    otherNames: string;
    rsaPin: string;
    designation: string;
    employmentDate: string;
}

type FormErrors = Partial<Record<keyof EmployeeFormData, string>>;

const NAME_REGEX = /^[a-zA-Z\s\-']+$/;

function validate(data: EmployeeFormData): FormErrors {
    const errors: FormErrors = {};

    const surname = data.surname.trim();
    if (!surname) errors.surname = "Surname is required.";
    else if (!NAME_REGEX.test(surname)) errors.surname = "Surname can only contain letters, spaces, hyphens, and apostrophes.";
    else if (surname.length > 50) errors.surname = "Surname must be 50 characters or fewer.";

    const otherNames = data.otherNames.trim();
    if (!otherNames) errors.otherNames = "Other names are required.";
    else if (!NAME_REGEX.test(otherNames)) errors.otherNames = "Other names can only contain letters, spaces, hyphens, and apostrophes.";
    else if (otherNames.length > 50) errors.otherNames = "Other names must be 50 characters or fewer.";

    if (!data.rsaPin.trim()) errors.rsaPin = "RSA PIN is required.";

    const designation = data.designation.trim();
    if (!designation) errors.designation = "Designation is required.";
    else if (designation.length > 100) errors.designation = "Designation must be 100 characters or fewer.";

    if (!data.employmentDate) {
        errors.employmentDate = "Employment date is required.";
    } else if (new Date(data.employmentDate) > new Date()) {
        errors.employmentDate = "Employment date cannot be in the future.";
    }

    return errors;
}

export const AddEmployeePage = () => {
    const navigate = useNavigate();
    const { mutate: createChangeRequest, isPending } = useCreateChangeRequest();

    const [formData, setFormData] = useState<EmployeeFormData>({
        surname: '',
        otherNames: '',
        rsaPin: '',
        designation: '',
        employmentDate: '',
    });
    const [touched, setTouched] = useState<Partial<Record<keyof EmployeeFormData, boolean>>>({});
    const [rsaPinError, setRsaPinError] = useState<string | null>(null);
    const [isValidatingRsaPin, setIsValidatingRsaPin] = useState(false);
    const [banner, setBanner] = useState<{ type: "info" | "warning" | "error"; message: string } | null>(null);

    const errors = validate(formData);
    const visibleErrors: FormErrors = Object.fromEntries(
        Object.entries(errors).filter(([key]) => touched[key as keyof EmployeeFormData])
    );
    const allFieldErrors = {
        ...visibleErrors,
        ...(touched.rsaPin && rsaPinError ? { rsaPin: rsaPinError } : {}),
    };
    const hasErrors = Object.keys(errors).length > 0 || !!rsaPinError;
    const canSubmit = !hasErrors && !isPending && !isValidatingRsaPin;

    const handleInputChange = (field: keyof EmployeeFormData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (field === 'rsaPin') setRsaPinError(null);
    };

    const handleBlur = useCallback((field: keyof EmployeeFormData) => {
        setTouched(prev => ({ ...prev, [field]: true }));
    }, []);

    const handleRsaPinBlur = useCallback(async () => {
        setTouched(prev => ({ ...prev, rsaPin: true }));
        const pin = formData.rsaPin.trim();
        if (!pin) return;
        setIsValidatingRsaPin(true);
        try {
            const result = await ChangeRequestService.validateRsaPin(pin);
            if (!result.formatValid) setRsaPinError("RSA PIN format is invalid.");
            else if (!result.unique) setRsaPinError("This RSA PIN is already registered.");
            else setRsaPinError(null);
        } catch {
            setRsaPinError(null);
        } finally {
            setIsValidatingRsaPin(false);
        }
    }, [formData.rsaPin]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setTouched({ surname: true, otherNames: true, rsaPin: true, designation: true, employmentDate: true });
        if (!canSubmit) return;

        createChangeRequest(
            {
                type: ChangeRequestType.ADDITION,
                additionPayload: {
                surname: formData.surname.trim(),
                otherNames: formData.otherNames.trim(),
                rsaPin: formData.rsaPin.trim(),
                designation: formData.designation.trim(),
                employmentDate: formData.employmentDate,
                }
            },
            {
                onSuccess: () => navigate(PencomRoutes.EMPLOYEE_LIST),
                onError: () => setBanner({
                    type: "error",
                    message: "Failed to submit employee addition request. Please try again.",
                }),
            }
        );
    };

    return (
        <div className="w-full flex flex-col min-h-screen">
            <div className="w-full flex flex-col items-center justify-center max-w-[635px] mx-auto mt-6">
                {banner && (
                    <PencomBanner
                        type={banner.type}
                        title={banner.message}
                        onClose={() => setBanner(null)}
                        className="w-full mb-4"
                    />
                )}
                <div className="flex flex-col gap-y-4 items-center justify-center mt-8">
                    <h2 className="font-clash-display text-2xl font-semibold text-medium-dark-grey">Add New Employee</h2>
                    <p className="font-clash-display text-sm font-regular text-medium-dark-grey">Provide employee details to add them to your organisation.</p>
                </div>
                <form onSubmit={handleSubmit} className="mt-10 gap-y-6 flex flex-col w-full">
                    <div className="flex flex-row gap-x-11">
                        {/* Left Column */}
                        <div className="flex flex-col gap-y-6 flex-1">
                            <PencomTextField
                                id="surname"
                                label="Surname"
                                placeholder="Enter surname"
                                type="text"
                                value={formData.surname}
                                onChange={(e) => handleInputChange('surname', e.target.value)}
                                onBlur={() => handleBlur('surname')}
                                error={!!allFieldErrors.surname}
                                helperText={allFieldErrors.surname}
                            />

                            <PencomTextField
                                id="rsaPin"
                                label="RSA PIN"
                                placeholder="Enter RSA PIN"
                                type="text"
                                value={formData.rsaPin}
                                onChange={(e) => handleInputChange('rsaPin', e.target.value)}
                                onBlur={handleRsaPinBlur}
                                error={!!allFieldErrors.rsaPin}
                                helperText={isValidatingRsaPin ? "Validating…" : (allFieldErrors.rsaPin ?? undefined)}
                            />

                            <div className="flex flex-col gap-y-1">
                                <PencomDateField
                                    id="employmentDate"
                                    label="Employment Date"
                                    placeholder="Select date"
                                    value={formData.employmentDate}
                                    onChange={(value) => {
                                        handleInputChange('employmentDate', value);
                                        handleBlur('employmentDate');
                                    }}
                                />
                                {allFieldErrors.employmentDate && (
                                    <p className="text-red-500 text-md font-clash-display">{allFieldErrors.employmentDate}</p>
                                )}
                            </div>
                        </div>

                        {/* Right Column */}
                        <div className="flex flex-col gap-y-6 flex-1">
                            <PencomTextField
                                id="otherNames"
                                label="Other Names"
                                placeholder="Enter other names"
                                type="text"
                                value={formData.otherNames}
                                onChange={(e) => handleInputChange('otherNames', e.target.value)}
                                onBlur={() => handleBlur('otherNames')}
                                error={!!allFieldErrors.otherNames}
                                helperText={allFieldErrors.otherNames}
                            />

                            <PencomTextField
                                id="designation"
                                label="Designation"
                                placeholder="Enter designation"
                                type="text"
                                value={formData.designation}
                                onChange={(e) => handleInputChange('designation', e.target.value)}
                                onBlur={() => handleBlur('designation')}
                                error={!!allFieldErrors.designation}
                                helperText={allFieldErrors.designation}
                            />
                        </div>
                    </div>

                    <div className="flex justify-center mt-8">
                        <PrimaryButton
                            title="Add Employee"
                            onClick={() => {}}
                            isLoading={isPending}
                            disabled={!canSubmit}
                            className="w-full"
                        />
                    </div>
                </form>
            </div>
        </div>
    )
}
